import type { SxProps, Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { CSSProperties } from "react";
import { ACCENT_STRIP_HEIGHT, ALPHA, CONTROL_HEIGHT, FONT_SIZE, ICON_SIZE, INK, INK_BLACK, MOTION, SURFACE } from "../../theme/tokens";
import type { GroupedActivityLogChangeResult, GroupedActivityLogClientTimestamps, InHouseRefreshOptions, StoredGroupedActivityLog } from "../../utils/models";

export const getRefreshOptionsByGroupedActivityLog = (refreshStatusData: GroupedActivityLogChangeResult): InHouseRefreshOptions => {
	return {
		avoidRefreshFor: {
			departmentData: refreshStatusData && refreshStatusData.departmentData.changeCount === 0,
			nursesData: refreshStatusData && refreshStatusData.nurses.changeCount === 0,
			notificationsData: refreshStatusData && refreshStatusData.notifications.changeCount === 0,
			requestsData: refreshStatusData && refreshStatusData.requests.changeCount === 0,
			wageData: refreshStatusData && refreshStatusData.wage.changeCount === 0,
		},
	};
};

export const getGroupedActivityLogClientTimestamps = (refreshStatusData: StoredGroupedActivityLog | null): GroupedActivityLogClientTimestamps | null => {
	return refreshStatusData
		? {
				requests: refreshStatusData.requests.lastChange.toISO() as string,
				notifications: refreshStatusData.notifications.lastChange.toISO() as string,
				nurses: refreshStatusData.nurses.lastChange.toISO() as string,
				departmentData: refreshStatusData.departmentData.lastChange.toISO() as string,
				wage: refreshStatusData.wage.lastChange.toISO() as string,
			}
		: null;
};

/**
 * Themed AppBar style — function form so MUI threads the live theme in and
 * the gradient retints when the user picks a new primary color.
 */
export const appBarSx: SxProps<Theme> = (theme: Theme) => ({
	background: `linear-gradient(180deg, ${theme.palette.primary.dark} -71.43%, ${theme.palette.primary.light} 194.28%)`,
	boxShadow: "none",
});

/** Floor height of the top bar's single row — the bar itself grows with its 40px controls. */
const TOOLBAR_MIN_HEIGHT = 48;

/**
 * Top bar row, plus the hairline warning strip that marks "wages are hidden" — the whole-app reminder
 * that a view-wide mode is on, kept to the bar's bottom edge instead of recoloring the bar.
 *
 * It hangs off the Toolbar rather than the AppBar because the AppBar's own `position` is load-bearing
 * (`fixed` in the native shell, `static` on web), so it can't be made a containing block; the Toolbar
 * is the bar's only in-flow child, so its bottom edge *is* the bar's bottom edge.
 *
 * The strip is always painted and only faded, for two reasons: an element created on toggle has no
 * previous value to transition from, and keeping it in the box means neither state can shift layout.
 */
export const toolbarSx =
	(isWageHidden: boolean): SxProps<Theme> =>
	(theme: Theme) => ({
		minHeight: TOOLBAR_MIN_HEIGHT,
		px: 2,
		position: "relative",
		"&::after": {
			content: '""',
			position: "absolute",
			left: 0,
			right: 0,
			bottom: 0,
			height: ACCENT_STRIP_HEIGHT,
			backgroundColor: theme.palette.warning.main,
			opacity: isWageHidden ? 1 : 0,
			transition: `opacity ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}`,
			pointerEvents: "none",
		},
	});

export const headerStyles: Record<string, CSSProperties | SxProps> = {
	logo: {
		textDecoration: "none",
		marginRight: "4px",
	},
	logoImgContainer: {
		position: "relative",
		height: "50px",
		cursor: "pointer",
		maxWidth: "fit-content",
		width: {
			xs: 100,
			sm: "fit-content",
		},
		display: "flex",
		alignItems: "center",
		":hover": {
			backgroundColor: alpha(SURFACE, ALPHA.SHADOW_SOFT),
			borderRadius: "8px",
		},
	},
	logoImg: {
		marginLeft: "1vw",
		marginRight: "1vw",
		maxWidth: "100%",
		maxHeight: "100%",
	},
	pageTitle: {
		color: "white",
		fontWeight: 600,
		fontSize: "1.125rem",
		ml: 1,
	},
	divider: {
		my: 1,
		bgcolor: "white",
		borderColor: "white",
	},
	mobileButton: {
		textTransform: "none",
		fontSize: "0.875rem",
	},
	tabButton: {
		minWidth: "auto",
		px: 1.5,
		py: 0.5,
		fontWeight: "bold",
		mr: 0.5,
		"&:hover": { bgcolor: alpha(SURFACE, ALPHA.SHADOW_MEDIUM) },
	},
	/**
	 * The right-hand control group. Nothing in the top bar was misaligned (the row centres to within
	 * 0.2px) but the seven controls in it measured 24-40px, and the smallest target was the hamburger
	 * that opens the whole secondary navigation. One height for the group, from the shared scale, so a
	 * control's hit area stops depending on which MUI primitive its author happened to reach for.
	 */
	actionGroup: {
		display: "flex",
		alignItems: "center",
		// The group owns the spacing between its controls. It used to come from `ml`/`mr` on two of them,
		// which left the run of box gaps at 0, 8, 8, 0, 8px.
		//
		// A uniform box gap is necessary but not sufficient: what the eye measures is ink to ink, and these
		// boxes are mostly transparent. With the boxes 2px apart the optical gaps still ran 32, 22, 20, 18,
		// 14px — a 2x spread — because the internal padding varied (the wage button carried a text button's
		// 20px, the rest 8-10px) and the glyphs ranged 20-24px. Same box, same glyph, one gap: the optical
		// gap is then 10 + 2 + 10 everywhere.
		gap: 0.25,
		"& .MuiSvgIcon-root": {
			fontSize: ICON_SIZE.CONTROL,
		},
		"& .MuiIconButton-root": {
			width: CONTROL_HEIGHT.STANDARD,
			height: CONTROL_HEIGHT.STANDARD,
		},
		"& .MuiButton-root": {
			minWidth: CONTROL_HEIGHT.STANDARD,
			minHeight: CONTROL_HEIGHT.STANDARD,
		},
	},
	departmentButton: {
		textTransform: "none",
		fontWeight: "bold",
		maxWidth: "100%",
		// 10px, matching where the glyph sits inside every icon button beside it (20px centred in a 40px
		// box), so this control's ink starts on the group's rhythm rather than 2px inside it.
		px: {
			xs: 0,
			sm: 1.25,
		},
		// MUI pulls a Button's `endIcon` 4px back past the padding (`marginRight: -4px`), which left the
		// chevron 4px from the box edge and made this the one visibly short gap in the row.
		"& .MuiButton-endIcon": {
			marginRight: 0,
		},
		py: {
			xs: 0,
			sm: 0.25,
		},
	},
	departmentGroupTitle: {
		px: 2,
		pt: 1,
		pb: 0.5,
		fontSize: "0.75rem",
		fontWeight: 600,
		textTransform: "uppercase",
		color: "text.secondary",
		letterSpacing: "0.04em",
	},
	departmentButtonText: {
		fontSize: "inherit",
		color: "inherit",
		maxWidth: {
			xs: "30px",
			sm: "fit-content",
		},
		textWrap: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	departmentButtonTextBedSide: {
		fontSize: "inherit",
		color: "inherit",
		maxWidth: {
			xs: "60px",
			sm: "fit-content",
		},
		textWrap: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	menuButton: {
		color: "white",
		p: 0,
	},
	drawer: {
		"& .MuiDrawer-paper": {
			width: 340,
			bgcolor: SURFACE,
			boxShadow: `-4px 0px 8px ${alpha(INK_BLACK, ALPHA.SHADOW_SOFT)}`,
		},
	},
	drawerContent: {
		p: 3,
	},
	listIcon: {
		color: INK.SECONDARY,
		minWidth: 40,
	},
	listItemText: {
		"& .MuiListItemText-primary": {
			color: INK.SECONDARY,
			fontWeight: 500,
		},
	},
	profileButton: {
		minWidth: "unset",
		width: 28,
		height: 28,
		borderRadius: "50%",
		p: 0,
		overflow: "hidden",
	},
	avatar: {
		width: 28,
		height: 28,
		fontSize: "0.875rem",
	},
	profileImage: {
		width: "100%",
		height: "100%",
		objectFit: "cover",
	},
};

/**
 * The row shape a menu or sheet that stands in for the header uses for its own entries.
 *
 * Shared because two components now draw rows into the same list — the gaps-first More sheet's quick
 * actions, and `WageRevealButton` in its `menuRow` variant, which is rendered BY that sheet and has to
 * be indistinguishable from the rows beside it. Kept here rather than in the sheet so the dependency
 * runs one way: the sheet already reaches into `header/` for `AdminDrawer`.
 */
export const menuRowStyles = {
	icon: { color: INK.SECONDARY, fontSize: ICON_SIZE.MENU_ROW },
	iconContainer: { minWidth: "40px" },
	text: { "& .MuiListItemText-primary": { fontSize: FONT_SIZE.ROW_LABEL, color: INK.SECONDARY } },
} as const satisfies Record<string, SxProps>;
